import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { getRequest } from "../../util/service";

const PROJECT_CACHE_PREFIX = "breadcrumb_project_";
const BATCH_CACHE_PREFIX = "breadcrumb_batch_";

const RESULT_ROUTES = {
  "glide-results": { label: "Glide Result", tab: "glide", parentSegment: "glide-results" },
  "phase-results": { label: "Pharmacophore Result", tab: "phase", parentSegment: "phase-results" },
  "adme-results": { label: "ADME Result", tab: "adme", parentSegment: "adme-results" },
};

const THREE_D_ROUTES = {
  glide3d: { label: "Glide Result", tab: "glide", stateKey: "glideId", parentSegment: "glide-results" },
  molecule3d: { label: "Pharmacophore Result", tab: "phase", stateKey: "phaseId", parentSegment: "phase-results" },
};

const readJson = (key) => {
  try {
    const value = sessionStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage failures; breadcrumbs can still fall back to route IDs.
  }
};

const compactState = (obj) => (
  Object.fromEntries(Object.entries(obj || {}).filter(([, value]) => value !== undefined && value !== null))
);

const normalizeTeamMembers = (members) => {
  if (!Array.isArray(members)) return [];
  return members
    .map((member) => member?.username || member?.name || member?.email || member)
    .filter(Boolean);
};

const normalizeProject = (source, fallbackProjectId) => {
  if (!source) return null;
  const projectId = source.projectId || source.p_record_id || fallbackProjectId;
  if (!projectId) return null;

  const project = {
    projectId,
    projectName: source.projectName || source.p_name || source.name || projectId,
    owners: source.owners || source.p_owner || [],
    teamMembers: normalizeTeamMembers(source.teamMembers || source.team_members),
    description: source.description || source.p_description || "",
  };

  return project;
};

const normalizeBatch = (source, fallbackBatchId) => {
  if (!source && !fallbackBatchId) return null;
  const batchId = source?.batchId || source?.b_record_id || fallbackBatchId;
  if (!batchId) return null;

  return {
    batchId,
    batchName: source?.batchName || source?.b_name || batchId,
    batchStatus: source?.batchStatus || source?.b_status || "",
    parent_batch_id: source?.parent_batch_id || null,
  };
};

const getProjectFromState = (state, fallbackProjectId) => {
  if (!state) return null;
  if (!state.projectName && !state.owners && !state.teamMembers && !state.description && !state.projectId) {
    return null;
  }
  return normalizeProject(state, fallbackProjectId);
};

const getBatchFromState = (state, fallbackBatchId) => {
  if (!state) return null;
  if (!state.batchStatus && !state.parent_batch_id && !state.batchName && !state.b_name) {
    return null;
  }
  return normalizeBatch(state, fallbackBatchId);
};

const getAttributeTitle = (state, isEdit) => {
  if (state?.isReTrigger) return "Re-Trigger Experiment";
  if (state?.isClone || state?.isCloneMode) return "Clone Experiment";
  return isEdit ? "Edit Generation" : "Submit New Generation";
};

export default function useBreadcrumbItems() {
  const params = useParams();
  const location = useLocation();
  const userName = useSelector((state) => state.header?.userName);
  const pathname = location.pathname;
  const segments = useMemo(() => pathname.split("/").filter(Boolean), [pathname]);
  const state = useMemo(() => location.state || {}, [location.state]);

  const projectId = params.projectId || state.projectId || (segments[1] === "result" ? segments[0] : undefined);
  const batchId = params.batchId || (segments[0] === "attributes" ? params.batchId : state.batchId);
  const recordId = params.recordId;

  const [projectContext, setProjectContext] = useState(null);
  const [batchContext, setBatchContext] = useState(null);

  useEffect(() => {
    if (!projectId) {
      setProjectContext(null);
      return;
    }

    const fromState = getProjectFromState(state, projectId);
    if (fromState) {
      setProjectContext(fromState);
      writeJson(`${PROJECT_CACHE_PREFIX}${projectId}`, fromState);
      return;
    }

    const cached = readJson(`${PROJECT_CACHE_PREFIX}${projectId}`);
    setProjectContext(cached || normalizeProject({ projectId }, projectId));
  }, [projectId, state]);

  useEffect(() => {
    if (!projectId || !userName) return;
    if (projectContext?.projectName && projectContext.projectName !== projectId) return;

    let cancelled = false;
    getRequest(`/project/user/${userName}`)
      .then((resp) => {
        if (cancelled) return;
        const projects = Array.isArray(resp?.data) ? resp.data : [];
        const match = projects.find((item) => String(item.p_record_id) === String(projectId));
        const normalized = normalizeProject(match, projectId);
        if (normalized) {
          setProjectContext(normalized);
          writeJson(`${PROJECT_CACHE_PREFIX}${projectId}`, normalized);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [projectId, userName, projectContext?.projectName]);

  useEffect(() => {
    if (!batchId) {
      setBatchContext(null);
      return;
    }

    const fromState = getBatchFromState(state, batchId);
    if (fromState) {
      setBatchContext(fromState);
      writeJson(`${BATCH_CACHE_PREFIX}${projectId || "unknown"}_${batchId}`, fromState);
      return;
    }

    const cached = readJson(`${BATCH_CACHE_PREFIX}${projectId || "unknown"}_${batchId}`);
    setBatchContext(cached || normalizeBatch(null, batchId));
  }, [batchId, projectId, state]);

  useEffect(() => {
    if (!projectId || !batchId) return;
    if (batchContext?.batchName && batchContext.batchName !== batchId && batchContext.batchStatus) return;

    let cancelled = false;
    getRequest(`/riva/moleculegenbatches/${projectId}`)
      .then((resp) => {
        if (cancelled) return;
        const batches = Array.isArray(resp?.data) ? resp.data : [];
        const match = batches.find((item) => String(item.b_record_id) === String(batchId));
        const normalized = normalizeBatch(match, batchId);
        if (normalized) {
          setBatchContext(normalized);
          writeJson(`${BATCH_CACHE_PREFIX}${projectId}_${batchId}`, normalized);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [projectId, batchId, batchContext?.batchName, batchContext?.batchStatus]);

  return useMemo(() => {
    const projectState = compactState({
      projectId,
      projectName: projectContext?.projectName,
      owners: projectContext?.owners,
      teamMembers: projectContext?.teamMembers,
      description: projectContext?.description,
    });
    const batchState = compactState({
      ...projectState,
      batchStatus: batchContext?.batchStatus,
      parent_batch_id: batchContext?.parent_batch_id,
    });
    const projectName = projectContext?.projectName || projectId;
    const projectLabel = projectId && projectName && projectName !== projectId
      ? `${projectName} (${projectId})`
      : projectName || projectId;
    const batchLabel = batchContext?.batchName || batchId;
    const items = [{ label: "Projects", to: "/create-project" }];

    if (pathname === "/create-project") {
      return [{ label: "Projects", current: true }];
    }

    if (projectId) {
      items.push({
        label: projectLabel || projectId,
        to: `/${projectId}/result`,
        state: projectState,
      });
    }

    if (segments[1] === "result") {
      return items.map((item, index) => index === items.length - 1 ? { ...item, current: true, to: undefined } : item);
    }

    if (segments[1] === "fragmentation") {
      items.push({ label: "Fragmentation", current: true });
      return items;
    }

    if (segments[0] === "feedback") {
      items.push({ label: "Provide Feedback", current: true });
      return items;
    }

    if (segments[0] === "attributes") {
      if (batchId) {
        items.push({
          label: batchLabel || batchId,
          to: projectId ? `/project/${projectId}/batch/${batchId}` : undefined,
          state: batchState,
        });
      }
      items.push({ label: getAttributeTitle(state, !!batchId), current: true });
      return items;
    }

    if (segments[0] === "project" && projectId && batchId) {
      const childRoute = segments[4];
      items.push({
        label: childRoute ? (batchLabel || batchId) : batchId,
        to: `/project/${projectId}/batch/${batchId}`,
        state: batchState,
      });

      if (!childRoute) {
        return items.map((item, index) => index === items.length - 1 ? { ...item, current: true, to: undefined } : item);
      }

      if (childRoute === "submit") {
        items.push({ label: "Create Selection", current: true });
        return items;
      }

      if (childRoute === "results") {
        items.push({ label: "Screening Results", current: true });
        return items;
      }

      const resultConfig = RESULT_ROUTES[childRoute];
      if (resultConfig) {
        items.push({
          label: "Screening Results",
          to: `/project/${projectId}/batch/${batchId}/results`,
          state: compactState({ ...projectState, defaultTab: resultConfig.tab }),
        });
        items.push({ label: `${resultConfig.label} / ${recordId || ""}`.trim(), current: true });
        return items;
      }

      const threeDConfig = THREE_D_ROUTES[childRoute];
      if (threeDConfig) {
        items.push({
          label: "Screening Results",
          to: `/project/${projectId}/batch/${batchId}/results`,
          state: compactState({ ...projectState, defaultTab: threeDConfig.tab }),
        });

        const parentRecordId = state?.[threeDConfig.stateKey];
        if (parentRecordId) {
          items.push({
            label: `${threeDConfig.label} / ${parentRecordId}`,
            to: `/project/${projectId}/batch/${batchId}/${threeDConfig.parentSegment}/${parentRecordId}`,
            state: compactState({ ...projectState, defaultTab: threeDConfig.tab }),
          });
        }

        items.push({ label: `3D Viewer / ${recordId || ""}`.trim(), current: true });
        return items;
      }
    }

    return items.length ? items.map((item, index) => index === items.length - 1 ? { ...item, current: true, to: undefined } : item) : [];
  }, [batchContext, batchId, pathname, projectContext, projectId, recordId, segments, state]);
}
